// CHG-1283 / ITER-3408 — global trigger for the v2 first-visit welcome.
// CHG-1469 / ITER-3586 — gated on isAuthenticated so the overlay never
//   intercepts /login, /404, the ProtectedRoute redirect destination, or
//   any other pre-auth surface.
// CHG-1649 / ITER-3758 — synchronous-state init + useLayoutEffect to
//   prevent a single-frame flash on first paint AND to make the
//   dismissed/completed flags load BEFORE React commits the initial
//   tree. Field test v3 reported the modal flashing on every route
//   navigation after CHG-1469; root cause was the prior useState=false
//   default + async useEffect read of localStorage, which left a paint
//   window where the modal could mount on top of the page even when the
//   donor had already dismissed it. Lazy initializer reads localStorage
//   eagerly in render so a returning visitor's first render is already
//   show=false. useLayoutEffect runs BEFORE the browser paints, so the
//   auth-gate result is committed pre-paint as well — no flash even on
//   unauth or still-hydrating sessions.
//
// Mounts via WelcomeMount in App.tsx (sibling to <CommandPalette />,
// route-scoped to /, /overview, /platform, /me roots so non-landing
// surfaces never have the modal in their DOM tree at all). Renders
// <WelcomeOverlay /> only when (a) the user is authenticated and (b)
// isWelcomeNeeded() returns true. The overlay lives at z-index 1000
// over the AppShell + routes.
//
// Side-effects: when the overlay closes, the trigger flips show=false
// so the overlay unmounts immediately. The localStorage flag write
// inside the overlay (markCompleted / markDismissed in storage.ts)
// ensures subsequent renders / page nav skip the overlay.

import { useLayoutEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { WelcomeOverlay } from "./WelcomeOverlay";
import { isWelcomeNeeded } from "./storage";

function readShowSync(): boolean {
  // SSR / non-browser execution (vitest jsdom is "browser-ish"; this
  // branch only fires when window is genuinely undefined). Default
  // false so the trigger never flashes on server-render output.
  if (typeof window === "undefined") return false;
  return isWelcomeNeeded();
}

export function WelcomeTrigger() {
  const { isAuthenticated, isLoading } = useAuth();
  // Lazy initializer reads localStorage synchronously at first render.
  // For a returning visitor with the dismissed/completed flag set, the
  // initial state is already false — no flash window between
  // useEffect-fires and overlay-hides. The auth-gate below can only
  // *lower* show from true to false; it never re-enables a hidden
  // overlay (the only true→true path is "auth resolves to true AND
  // welcome flag still unset", which is the legitimate first-time
  // visitor case).
  const [show, setShow] = useState<boolean>(readShowSync);

  // useLayoutEffect (vs useEffect) fires synchronously after DOM
  // mutations but BEFORE the browser paints. That closes the
  // remaining flash window where the trigger might paint show=true
  // for unauth users in the brief moment between mount and the
  // AuthProvider's token-hydration completing. The cost is a
  // synchronous render pass; cheap here because the effect body is
  // a single localStorage read + setState.
  useLayoutEffect(() => {
    if (isLoading || !isAuthenticated) {
      setShow(false);
      return;
    }
    // Re-read storage when auth resolves — if a returning visitor
    // landed here after a token refresh, the flag may have been
    // written by a prior tab.
    setShow(isWelcomeNeeded());
  }, [isLoading, isAuthenticated]);

  if (!isAuthenticated || !show) return null;
  return <WelcomeOverlay onClose={() => setShow(false)} />;
}
